import React, { useCallback, useState } from 'react';
import Cropper, { type Area, type MediaSize } from 'react-easy-crop';

type AspectChoice = number | 'original';

const ASPECT_PRESETS: { label: string; value: AspectChoice }[] = [
  { label: 'Original', value: 'original' },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '1:1', value: 1 },
];

type VenuePhotoCropModalProps = {
  imageUrl: string;
  title?: string;
  busy?: boolean;
  /**
   * Crop aspect ratio. Defaults to **1:1** — the app renders venue photos square everywhere
   * (`screens/venue-discovery.tsx` `aspectRatio: 1`, city grid cards likewise), so a venue
   * approval must be composed square. Was 4/3 until 2026-08; the 154 photos approved under the
   * old default are re-cut from their originals in the Re-Crop tab.
   *
   * City heroes pass this explicitly too, and as of 2026-08-01 they also pass **1**, not 16/9 —
   * `screens/city.tsx:3565` renders the city hero `aspectRatio: 1`. See `CITY_HERO_CROP_ASPECT`
   * in `src/components/CityHeroPickerModal.tsx`, which is the source of truth for that value.
   */
  aspect?: number;
  /** Hide the ratio presets so the crop cannot leave `aspect` (used by the square Re-Crop flow). */
  lockAspect?: boolean;
  /** Confirm-button label. Defaults to the approval wording. */
  confirmLabel?: string;
  /** Confirm-button label while `busy`. Defaults to the approval wording. */
  busyLabel?: string;
  onCancel: () => void;
  onConfirm: (pixelCrop: Area) => void | Promise<void>;
};

export function VenuePhotoCropModal({
  imageUrl,
  title = 'Crop photo',
  busy = false,
  aspect = 1,
  lockAspect = false,
  confirmLabel,
  busyLabel,
  onCancel,
  onConfirm,
}: VenuePhotoCropModalProps) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  /** The photo's own width/height ratio, learned once it loads — drives the "Original" option. */
  const [naturalAspect, setNaturalAspect] = useState<number | null>(null);
  const [aspectChoice, setAspectChoice] = useState<AspectChoice>(aspect);

  const onCropComplete = useCallback((_area: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const onMediaLoaded = useCallback((media: MediaSize) => {
    if (media.naturalWidth > 0 && media.naturalHeight > 0) {
      setNaturalAspect(media.naturalWidth / media.naturalHeight);
    }
  }, []);

  /** "Original" maps to the photo's real ratio so the box covers the whole image (full width + height). */
  const effectiveAspect = aspectChoice === 'original' ? naturalAspect ?? aspect : aspectChoice;

  const selectAspect = useCallback((value: AspectChoice) => {
    setAspectChoice(value);
    /** Reset pan/zoom so the resized box snaps cleanly to the new ratio. */
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 p-4"
      role="presentation"
      onClick={onCancel}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="venue-crop-title"
        className="flex w-full max-w-3xl flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-xl max-h-[95vh]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border px-5 py-4 shrink-0">
          <h2 id="venue-crop-title" className="font-display text-lg font-semibold text-white tracking-tight">
            {title}
          </h2>
          <p className="text-xs text-mutedDim mt-1.5">Drag to reposition · scroll or slider to zoom</p>
        </div>

        <div className="relative h-[min(55vh,520px)] w-full shrink-0 bg-black">
          <Cropper
            image={imageUrl}
            crop={crop}
            zoom={zoom}
            aspect={effectiveAspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            onMediaLoaded={onMediaLoaded}
            objectFit="contain"
          />
        </div>

        <div className="border-t border-border px-5 py-4 space-y-4 shrink-0">
          <div className={`flex items-center gap-3 text-xs text-muted ${lockAspect ? 'hidden' : ''}`}>
            <span className="shrink-0 w-10">Ratio</span>
            <div className="flex flex-wrap gap-2">
              {ASPECT_PRESETS.map((preset) => {
                const active = aspectChoice === preset.value;
                return (
                  <button
                    key={preset.label}
                    type="button"
                    disabled={busy}
                    onClick={() => selectAspect(preset.value)}
                    className={`rounded-md border px-3 py-1.5 text-xs transition-colors disabled:opacity-50 ${
                      active
                        ? 'border-accent bg-accent/15 text-white'
                        : 'border-border text-muted hover:bg-white/[0.04]'
                    }`}
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
          </div>
          <label className="flex items-center gap-3 text-xs text-muted">
            <span className="shrink-0 w-10">Zoom</span>
            <input
              type="range"
              min={1}
              max={3}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-accent"
            />
          </label>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              disabled={busy}
              onClick={onCancel}
              className="rounded-lg border border-border px-4 py-2 text-sm text-white hover:bg-white/[0.04] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={busy || !croppedAreaPixels}
              onClick={() => {
                if (croppedAreaPixels) void onConfirm(croppedAreaPixels);
              }}
              className="rounded-lg bg-green-600/90 px-5 py-2 text-sm font-semibold text-white hover:bg-green-600 disabled:opacity-50"
            >
              {busy ? (busyLabel ?? 'Approving…') : (confirmLabel ?? 'Approve crop')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
